import { forwardRef, useState } from 'react';
import type { ImgHTMLAttributes, SyntheticEvent } from 'react';
import { optimizedImageUrl, type ImageWidth } from '../../services/imageUrl';

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width'> {
    src: string | null | undefined;
    /** Bredden att hämta, ungefär dubbla visningsbredden (skarpt på mobil). */
    width: ImageWidth;
    /** Egen bild för mobil (≤ 768 px), som i hero-bilderna. */
    mobileSrc?: string;
}

/**
 * En bild i butiken, hämtad i rätt storlek via Vercel (services/imageUrl.ts).
 * Går den förminskade bilden inte att hämta visas originalet, så att ingen
 * bild någonsin blir trasig på grund av förminskningen.
 */
export const StoreImage = forwardRef<HTMLImageElement, Props>(function StoreImage({ src, width, mobileSrc, onError, ...rest }, ref) {
    const [failedKey, setFailedKey] = useState<string | null>(null);
    const key = `${src}|${mobileSrc ?? ''}`;
    const optimized = optimizedImageUrl(src, width);
    const original = src ?? '';
    const optimizedMobile = mobileSrc ? optimizedImageUrl(mobileSrc, 1080) : undefined;
    const useOriginal = failedKey === key || (optimized === original && optimizedMobile === mobileSrc);

    const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
        if (!useOriginal) {
            setFailedKey(key);
            return;
        }
        onError?.(e);
    };

    const img = <img ref={ref} {...rest} src={(useOriginal ? original : optimized) || undefined} onError={handleError} />;
    if (!mobileSrc) return img;
    return (
        <picture>
            <source media="(max-width: 768px)" srcSet={useOriginal ? mobileSrc : optimizedMobile} />
            {img}
        </picture>
    );
});
